/**
 * Battle glass stage — pure geometry (no Phaser import, vitest-driven).
 *
 * The battle composition renders in-canvas (DOM elements cannot sit
 * below the opaque Phaser canvas), so the "Liquid Glass back panel"
 * behind the battlers is approximated Phaser-side:
 *
 *   1. Baked blur chain — the frozen-world snapshot is downsampled
 *      through successive half-size DynamicTextures with LINEAR
 *      filtering, then the smallest level is stretched back to full
 *      canvas size. Bilinear up-scale of a 1/8-res image reads as a
 *      ~12px frosted blur (the DOM `--glass-blur` twin) at zero
 *      per-frame cost — one bake at battle start, no live filter.
 *   2. Scrim wash — full-canvas SCRIM_WASH rect (the `.draft-modal`
 *      recipe: body-bg hue at 0.45), pushing the world back.
 *   3. Stage panel — one rectilinear GLASS_FILL_THIN rounded rect
 *      (8px — the anchored-to-canvas radius tier shared with the
 *      battle log + status panels) with a 1px SPECULAR_EDGE top
 *      inset, containing both battlers. Sprites draw above it,
 *      unfiltered, per the Zone 3 rule: glass may appear BELOW pixel
 *      art in z-order, never in front of it.
 *
 * This module computes the geometry; battleStage.ts owns the Phaser
 * objects.
 */

/** Canvas-edge margin of the stage panel (px). Sprites sit at a 24px
 *  margin, so they inset 8px inside the panel's own 16px margin. */
const PANEL_MARGIN = 16;
/** Breathing room between the player sprite's feet and the panel's
 *  bottom edge (px). Exactly the sprite→battle-log clearance
 *  BattleScene.layout() engineers (logClearanceCap = log top − 8), so
 *  when that cap binds (every phone-portrait canvas) the panel's
 *  bottom edge lands flush with the log's top edge instead of tucking
 *  under it in a glass-on-glass seam. */
const PANEL_FOOT_GAP = 8;
/** Anchored-to-canvas radius tier (battle log / status panel / dialogue). */
const PANEL_RADIUS = 8;
/** Never let a degenerate canvas collapse the panel below this height. */
const PANEL_MIN_HEIGHT = 64;

export interface StageLayoutInput {
  width: number;
  height: number;
  /** Bottom edge (y) of the player sprite — `playerSpriteY` in
   *  BattleScene.layout(), since the sprite uses setOrigin(0, 1). */
  playerSpriteBottom: number;
}

export interface StageLayout {
  panel: { x: number; y: number; width: number; height: number; radius: number };
  /** 1px specular highlight along the panel's top edge, inset past the
   *  rounded corners. y is pre-offset by 0.5 for crisp 1px stroking. */
  specular: { x1: number; x2: number; y: number };
}

export function computeStageLayout(input: StageLayoutInput): StageLayout {
  const { width, height, playerSpriteBottom } = input;
  const x = PANEL_MARGIN;
  const y = PANEL_MARGIN;
  const panelWidth = Math.max(PANEL_MIN_HEIGHT, width - PANEL_MARGIN * 2);
  const bottom = Math.min(playerSpriteBottom + PANEL_FOOT_GAP, height - PANEL_MARGIN);
  const panelHeight = Math.max(PANEL_MIN_HEIGHT, bottom - y);
  return {
    panel: { x, y, width: panelWidth, height: panelHeight, radius: PANEL_RADIUS },
    specular: {
      x1: x + PANEL_RADIUS,
      x2: x + panelWidth - PANEL_RADIUS,
      y: y + 0.5,
    },
  };
}

export interface BlurLevelSpec {
  /** Appended to the snapshot texture key — unique per level. */
  keySuffix: string;
  width: number;
  height: number;
}

/** Round up to the next even integer. DynamicTexture.setSize defaults
 *  forceEven=true and bumps odd dimensions UP by one — if the chain
 *  requested odd sizes, the allocated texture would be 1px larger than
 *  the stamp's coverage, leaving a transparent gutter that the next
 *  upsample smears into visible edge fringe. Pre-evening keeps the
 *  requested and allocated sizes identical. */
const evenUp = (n: number): number => (n % 2 === 0 ? n : n + 1);

/** Successive half-size levels (1/2 → 1/4 → 1/8). Halving per step keeps
 *  every bilinear downsample inside its 2×2 tap window (no skipped
 *  texels), so the chain approximates a gaussian rather than aliasing. */
export function computeBlurChain(input: { width: number; height: number }): BlurLevelSpec[] {
  const levels: BlurLevelSpec[] = [];
  let w = input.width;
  let h = input.height;
  for (let i = 1; i <= 3; i++) {
    w = Math.max(8, evenUp(Math.round(w / 2)));
    h = Math.max(8, evenUp(Math.round(h / 2)));
    levels.push({ keySuffix: `:blur${i}`, width: w, height: h });
  }
  return levels;
}
